// `looseObject` and `wireUnion` throughout: see `entities/media-campaign/api/schemas.ts` for why.
import { z } from 'zod';
import { wireUnion } from '@/lib/api/parse-response';
import type {
  AbbreviationSuggestionsResult,
  LinkedSchemaConfig,
  Taxonomy,
  TaxonomyChangeLogEntry,
  TaxonomyCreator,
  TaxonomyField,
  TaxonomyFieldValue,
  TaxonomyListRef,
  TaxonomyOutput,
  TaxonomyRule,
  TaxonomyRuleBranch,
  TaxonomyRuleCondition,
  TaxonomyRuleConditionGroup,
  TaxonomySettings,
  TaxonomySummary,
  TaxonomyVersion,
  TrackingCodePart,
} from '../model/types';

/** Stored JSONB passed `@IsOptional()`, which admits null, so a key typed as absent can be null. */
function storedOptional<T extends z.ZodType>(schema: T) {
  return schema.nullish().transform((value) => value ?? undefined);
}

const creatorSchema: z.ZodType<TaxonomyCreator> = z.looseObject({
  id: z.string(),
  // Platform's user type says string, but nothing checks it; a blank or missing name reads as
  // null so the callers' fallbacks apply.
  name: z
    .string()
    .nullish()
    .transform((value) => value?.trim() || null),
  avatarUrl: z.string().nullable().optional(),
});

const fieldValueSchema: z.ZodType<TaxonomyFieldValue> = z.looseObject({
  value: z.string(),
  code: z.string(),
});

const trackingCodePartSchema: z.ZodType<TrackingCodePart> = z.looseObject({
  kind: wireUnion<TrackingCodePart['kind']>(),
  fieldId: z.string().nullable().optional(),
  source: wireUnion<'code' | 'value'>().nullable().optional(),
  text: z.string().nullable().optional(),
  format: z.string().nullable().optional(),
  length: z.number().nullable().optional(),
  charset: wireUnion<'numeric' | 'alphanumeric'>().nullable().optional(),
  mode: wireUnion<'counter' | 'random'>().nullable().optional(),
});

const linkedSchemaConfigSchema: z.ZodType<LinkedSchemaConfig> = z.looseObject({
  targetSchemaId: z.string().nullable().optional(),
  pullKind: storedOptional(wireUnion<'tracking_code' | 'field'>()),
  pullFieldId: z.string().nullable().optional(),
  pullSource: wireUnion<'code' | 'value'>().nullable().optional(),
  includeInName: storedOptional(z.boolean()),
});

const listRefSchema: z.ZodType<TaxonomyListRef> = z.looseObject({
  listId: z.string(),
  listVersionId: z.string().nullable().optional(),
  pinnedBy: wireUnion<'publish'>().nullable().optional(),
  valueColumnId: z.string(),
  codeColumnId: z.string().nullable().optional(),
});

const fieldSchema: z.ZodType<TaxonomyField> = z.looseObject({
  id: z.string(),
  name: z.string(),
  group: wireUnion<TaxonomyField['group']>(),
  type: wireUnion<TaxonomyField['type']>(),
  required: z.boolean(),
  description: z.string().nullable().optional(),
  visible: storedOptional(z.boolean()),
  unique: storedOptional(z.boolean()),
  defaultValue: z.string().nullable().optional(),
  validationPattern: z.string().nullable().optional(),
  transform: wireUnion<NonNullable<TaxonomyField['transform']>>().nullable().optional(),
  level: z.string().nullable().optional(),
  parentId: z.string().nullable().optional(),
  multiselect: storedOptional(z.boolean()),
  valueWidth: storedOptional(z.number()),
  codeWidth: storedOptional(z.number()),
  format: z.string().nullable().optional(),
  pattern: storedOptional(z.array(trackingCodePartSchema)),
  link: storedOptional(linkedSchemaConfigSchema),
  libraryKey: z.string().nullable().optional(),
  listRef: listRefSchema.nullable().optional(),
  values: z.array(fieldValueSchema),
});

const ruleConditionSchema: z.ZodType<TaxonomyRuleCondition> = z.looseObject({
  fieldId: z.string(),
  operator: wireUnion<TaxonomyRuleCondition['operator']>(),
  value: z.string(),
});

const ruleConditionGroupSchema: z.ZodType<TaxonomyRuleConditionGroup> = z.lazy(() =>
  z.looseObject({
    combinator: wireUnion<TaxonomyRuleConditionGroup['combinator']>(),
    conditions: z.array(ruleConditionSchema),
    // The server's tree validator accepts a group without `groups` as a leaf.
    groups: z.array(ruleConditionGroupSchema).default([]),
  }),
);

const ruleBranchSchema: z.ZodType<TaxonomyRuleBranch> = z.looseObject({
  id: z.string(),
  when: ruleConditionGroupSchema,
  action: wireUnion<TaxonomyRuleBranch['action']>(),
  targetFieldId: z.string(),
  values: storedOptional(z.array(z.string())),
  defaultValue: z.string().nullable().optional(),
});

const ruleSchema: z.ZodType<TaxonomyRule> = z.looseObject({
  id: z.string(),
  branches: storedOptional(z.array(ruleBranchSchema)),
  conditions: storedOptional(z.array(ruleConditionSchema)),
  action: storedOptional(wireUnion<NonNullable<TaxonomyRule['action']>>()),
  targetFieldId: storedOptional(z.string()),
  values: storedOptional(z.array(z.string())),
  defaultValue: z.string().nullable().optional(),
});

const settingsSchema: z.ZodType<TaxonomySettings> = z.looseObject({
  fieldDelimiter: z.string().nullable().optional(),
  subfieldDelimiter: z.string().nullable().optional(),
  channelLimits: storedOptional(
    z.array(z.looseObject({ platform: z.string(), limit: z.number() })),
  ),
  codeCap: z.number().nullable().optional(),
  trackingCode: storedOptional(
    z.looseObject({ pattern: storedOptional(z.array(trackingCodePartSchema)) }),
  ),
});

const outputSchema: z.ZodType<TaxonomyOutput> = z.looseObject({
  id: z.string(),
  name: z.string(),
  level: z.string().nullable().optional(),
  parts: z.array(trackingCodePartSchema),
  delimiter: z.string().nullable().optional(),
  locked: storedOptional(z.boolean()),
  requireUnique: storedOptional(z.boolean()),
  isPrimary: storedOptional(z.boolean()),
});

export const taxonomyVersionSchema: z.ZodType<TaxonomyVersion> = z.looseObject({
  id: z.string(),
  taxonomyId: z.string(),
  versionNumber: z.number(),
  revision: z.number(),
  parentVersionId: z.string().nullable(),
  status: wireUnion<TaxonomyVersion['status']>(),
  market: z.array(z.string()),
  platform: z.array(z.string()),
  fields: z.array(fieldSchema),
  rules: z.array(ruleSchema),
  settings: settingsSchema,
  outputs: z.array(outputSchema),
  description: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
});

const taxonomySummarySchema: z.ZodType<TaxonomySummary> = z.looseObject({
  id: z.string(),
  organizationId: z.string(),
  workspaceId: z.string().nullable(),
  name: z.string(),
  scope: wireUnion<TaxonomySummary['scope']>(),
  currentVersionId: z.string().nullable(),
  version: z.number(),
  currentVersionNumber: z.number().nullable(),
  status: wireUnion<NonNullable<TaxonomySummary['status']>>().nullable(),
  market: z.array(z.string()),
  platform: z.array(z.string()),
  createdBy: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  creator: creatorSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const taxonomySummaryListSchema = z.array(taxonomySummarySchema);

export const taxonomySchema: z.ZodType<Taxonomy> = z.looseObject({
  id: z.string(),
  organizationId: z.string(),
  workspaceId: z.string().nullable(),
  name: z.string(),
  scope: wireUnion<Taxonomy['scope']>(),
  currentVersionId: z.string().nullable(),
  version: z.number(),
  createdBy: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  creator: creatorSchema.nullable().optional(),
  modifiedBy: creatorSchema.nullable().optional(),
  versions: z.array(taxonomyVersionSchema),
  trackingCodeCount: z.number().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const changeLogEntrySchema: z.ZodType<TaxonomyChangeLogEntry> = z.looseObject({
  id: z.string(),
  taxonomyId: z.string(),
  versionId: z.string().nullable(),
  actorId: z.string(),
  actor: creatorSchema.nullable().optional(),
  action: wireUnion<TaxonomyChangeLogEntry['action']>(),
  reason: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.string(),
});

export const taxonomyChangeLogSchema = z.array(changeLogEntrySchema);

export const abbreviationSuggestionsSchema: z.ZodType<AbbreviationSuggestionsResult> =
  z.looseObject({
    suggestions: z.array(
      z.looseObject({
        fieldId: z.string(),
        fieldName: z.string(),
        value: z.string(),
        from: z.string(),
        to: z.string(),
        reason: z.string(),
      }),
    ),
    formatChanges: z.array(
      z.looseObject({
        fieldId: z.string(),
        fieldName: z.string(),
        from: z.string(),
        to: z.string(),
        saved: z.number(),
        lossy: z.boolean(),
      }),
    ),
    unlinkChanges: z.array(
      z.looseObject({
        fieldId: z.string(),
        fieldName: z.string(),
        listName: z.string(),
        schemaCount: z.number(),
        fromMaxLength: z.number(),
        toMaxLength: z.number(),
        values: z.array(fieldValueSchema),
      }),
    ),
    currentLength: z.number(),
    targetLength: z.number(),
    estimatedLength: z.number(),
    fitsTarget: z.boolean(),
    shortfall: z.number(),
    sharedListPotential: z.number(),
    sharedListsIncluded: z.boolean(),
  });

export const bulkDeleteTaxonomiesSchema = z.looseObject({ deletedIds: z.array(z.string()) });
